import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { useContact, telHref } from "@/lib/contact";
import { trackEvent } from "@/lib/api";
import { Wordmark } from "@/components/marketing/Wordmark";

const COLS = [
  { title: "Academics", links: [
    ["Course Catalog", "/courses"],
    ["Live Classes", "/#live-classes"],
    ["Course Tuition", "/#how-you-learn"],
    ["Academic Resources", "/resources"],
  ]},
  { title: "Student Resources", links: [
    ["Student Portal", "/portal"],
    ["Sleep Observation Tracker", "/portal/progress"],
    ["Learning Center", "/portal/resources"],
    ["Admissions", "/register"],
  ]},
  { title: "Our Institution", links: [
    ["Meet Andrea", "/#instructor"],
    ["About Our Courses", "/how-it-works"],
    ["How Classes Work", "/how-it-works"],
    ["Contact", "/contact"],
  ]},
];
const PRIVATE = [["Private Consulting", "/pricing"], ["Infant Sleep Diagnostic", "/infant-sleep-diagnostic"], ["Personalized Sleep Blueprint", "/personalized-infant-sleep-blueprint"],
  ["Guided Virtual Consulting", "/guided-infant-sleep-consulting"], ["In-Home Consulting", "/in-home-infant-sleep-consulting"], ["The Nurtured Parent Co.", "https://thenurturedparentco.com/"]];

function FooterLink({ label, to }) {
  const cls = "text-sm text-[#A39E98] hover:text-[#F7F5F0] transition-colors";
  const tid = `footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  if (to.startsWith("http")) return <a href={to} target="_blank" rel="noopener" data-testid={tid} className={cls} onClick={() => trackEvent("outbound_consulting_click", { location: "footer" })}>{label}</a>;
  if (to.includes("#")) return <a href={to} data-testid={tid} className={cls}>{label}</a>;
  return <Link to={to} data-testid={tid} className={cls}>{label}</Link>;
}

export default function SiteFooter() {
  const { email, phone } = useContact();
  return (
    <footer className="night border-t border-[#2E2C2A] pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="max-w-xs">
            <div className="mb-5"><Wordmark testid="footer-wordmark" /></div>
            <p className="text-sm text-[#A39E98] leading-relaxed">
              Expert sleep guidance for parents. Five online infant sleep courses developed by Andrea Hurley, Certified Pediatric Sleep Consultant, with self-paced coursework that opens immediately after enrollment, optional faculty-led instruction, a free Infant Sleep Tracker and your own Infant Sleep Learning Center. No consulting purchase is ever required to complete a course.
            </p>
            <div className="mt-5 space-y-2">
              <a href={`mailto:${email}`} data-testid="footer-email" className="flex items-center gap-2 text-[13px] text-[#A39E98] hover:text-[#F7F5F0] transition-colors break-all"><Mail size={14} className="text-[#C2836B] shrink-0" /> {email}</a>
              <a href={telHref(phone)} data-testid="footer-phone" className="flex items-center gap-2 text-sm text-[#A39E98] hover:text-[#F7F5F0] transition-colors"><Phone size={14} className="text-[#C2836B]" /> {phone}</a>
            </div>
          </div>
          {COLS.map((c) => (
            <div key={c.title}>
              <div className="eyebrow text-[#738075] mb-4">{c.title}</div>
              <ul className="space-y-2.5">
                {c.links.map(([label, to]) => (
                  <li key={to}>
                    <FooterLink label={label} to={to} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 pt-6 border-t border-[#2E2C2A] flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-6 gap-y-2" data-testid="footer-private-consulting">
          <span className="eyebrow text-[#738075]">Separate service · Optional</span>
          {PRIVATE.map(([label, to]) => <FooterLink key={to} label={label} to={to} />)}
          <span className="text-xs text-[#6b665e]">Not part of course tuition and never required for coursework.</span>
        </div>
        <div className="mt-8 pt-8 border-t border-[#2E2C2A] flex flex-col sm:flex-row justify-between gap-4">
          <p className="text-xs text-[#6b665e] max-w-2xl leading-relaxed">
            Infant sleep education and consulting are not medical care. We do not diagnose or treat medical or sleep disorders, and we do not guarantee outcomes. Health concerns should be discussed with your pediatrician.
          </p>
          <p className="text-xs text-[#6b665e]">© {new Date().getFullYear()} InfantSleepConsultantNearMe.com</p>
        </div>
      </div>
    </footer>
  );
}
